import { useEffect, useState } from 'react';
import { Dropdown } from '@quero-delivery/quero-components-web';
import { toast } from 'react-toastify';
import { createPortal } from 'react-dom';
import { Modal, Text, useTheme } from '@querodelivery/uni-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUTurnUpLeft as faIcon } from '@fortawesome/pro-solid-svg-icons';
import type { Delivery } from '@/_features/deliveries/types';
import { useGetEntregadoresByAssociate } from '../../../_features/deliveries/api/get-entregadores-by-associate';
import styles from './modal-connect-delivery.module.scss';
import { Skeleton } from '@/_config/lib/react-native-skeleton';
import { pluralByQuantity } from '@/_utils/text';

interface ModalConnectDeliveryProps {
  opened: boolean;
  delivery: Pick<Delivery, '_id'>;
  onClose(): void;
  loading: boolean;
  disabled: boolean;
  onConnect(motoboyId: string): void;
}

function ModalConnectDelivery({
  opened,
  delivery,
  disabled,
  loading,
  onClose,
  onConnect,
}: ModalConnectDeliveryProps) {
  const { color } = useTheme();
  const [motoboysOptions, setMotoboysOptions] = useState([]);

  const {
    data: motoboys,
    isLoading: loadingMotoboys,
    error,
  } = useGetEntregadoresByAssociate(
    {
      entregaId: delivery._id,
    },
    {
      enabled: opened,
    },
  );

  useEffect(() => {
    if (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorMessage = (error as any).response?.data.error;
      toast.error(errorMessage);
    }
  }, [error]);

  useEffect(() => {
    if (motoboys) {
      const uniqueMotoboysMap = new Map<string, (typeof motoboys)[0]>();
      motoboys.forEach(motoboy => {
        if (!uniqueMotoboysMap.has(motoboy._id)) {
          uniqueMotoboysMap.set(motoboy._id, motoboy);
        }
      });
      const uniqueMotoboys = Array.from(uniqueMotoboysMap.values());

      const newMotoboys = uniqueMotoboys.map(motoboy => ({
        key: motoboy._id,
        text: (
          <div className={styles.dropdownTextItemBoxContainer}>
            <div className={styles.dropdownTextItemContainer}>
              <p>{`${motoboy.nome}   `} </p>
              <p>
                {motoboy.deliverymanDistanceToPlaceInMeters
                  ? Number(
                      (
                        motoboy.deliverymanDistanceToPlaceInMeters / 1000
                      ).toFixed(2),
                    ).toLocaleString('pt-BR')
                  : '- - -'}{' '}
                km da loja
              </p>
            </div>
            {motoboy.establishments?.map((establishment, index) => {
              const hasDelivery =
                establishment.deliveryQuantity &&
                establishment.deliveryQuantity > 0;
              if (!hasDelivery) return null;
              return (
                <div key={index} className={styles.dropdownTextItemContainer}>
                  <Text variant="bodyXs" tone="supporting">
                    {establishment.establishmentName}
                  </Text>
                  <Text variant="bodyXs" tone="supporting">
                    {establishment.deliveryQuantity || 0}{' '}
                    {pluralByQuantity(
                      'entrega',
                      establishment.deliveryQuantity || 0,
                    )}
                  </Text>
                </div>
              );
            })}
            {(motoboy.page === 'RETORNO_AO_ESTABELECIMENTO' ||
              motoboy.page === 'AGUARDANDO_FINALIZACAO') && (
              <div className={styles.statusBadge}>
                <FontAwesomeIcon
                  icon={faIcon}
                  size="xs"
                  color={color['color-text-highlight']}
                />
                <Text variant="bodyXs" tone="highlight" fontWeight="medium">
                  Retornando ao estabelecimento
                </Text>
              </div>
            )}
          </div>
        ),
        value: motoboy._id,
      }));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setMotoboysOptions(newMotoboys as any);
    }
  }, [motoboys]);

  if (!opened) return null;

  return createPortal(
    <Modal
      isOpen={opened}
      onClose={onClose}
      header="associar entregador"
      width={480}
    >
      {!loadingMotoboys ? (
        <h4 className={styles.modalConnectDeliveryLabel}>
          <p>
            {motoboys?.length || 0}
            {motoboys && motoboys?.length > 1
              ? ' Entregadores ativos'
              : ' Entregador ativo'}
          </p>
        </h4>
      ) : (
        <Skeleton
          height={17}
          width={200}
          className={styles.modalConnectDeliveryLabel}
        />
      )}
      <div className={styles.modalConnectDeliveryDropdownUpperContainer}>
        <div className={styles.modalConnectdeliveryDropdownContainer}>
          <h4 className={styles.modalCoonectDeliveryDropdownLabel}>
            <p>Associar entregador </p>
          </h4>
          <Dropdown
            text="Selecionar"
            options={motoboysOptions}
            placeholder="Entregadores"
            fluid
            selectOnNavigation={false}
            selectOnBlur={false}
            value=""
            disabled={loadingMotoboys || loading || disabled}
            noResultsMessage="Nenhum entregador encontrado"
            loading={loadingMotoboys}
            onChange={(ev, data) => {
              onConnect(data.value as string);
              onClose();
            }}
          />
        </div>
      </div>
    </Modal>,
    document.body,
  );
}

export default ModalConnectDelivery;
